import * as Rx from 'rxjs'
import * as url from 'url'

import { Comment } from '../../states/CommentState'
import { Config } from '../../states/WorldState'
import { LoginResponse, MapObject, Player, User } from './payloads'
import { Point } from '../../models/BasicTypes'
import {
  parseComment,
  parseComments,
  parseLoginResponse,
  parseMapObject,
  parsePlayer,
  parseUser
} from './parsing'

export default class APIClient {
  sessionID: string | null = null

  /// API key of APIClient
  private _apiKey: string | null = null
  private _authHeader: string | null = null
  private appVersion: string | null = null
  private baseURL: string

  get apiKey (): string | null {
    return this._apiKey
  }

  set apiKey (newKey: string | null) {
    this._apiKey = newKey
    if (newKey) {
      this._authHeader = 'Basic ' + btoa(`${this.apiKey}:`)
    } else {
      this._authHeader = null
    }
  }

  constructor (baseURL: string, appVersion: string | null = null) {
    this.baseURL = baseURL
    this.appVersion = appVersion
  }

  /**
   * Get client configuration
   */
  getConfig (): Rx.Observable<Config> {
    return this.sendRequest({
      path: '/client_settings'
    })
      .map(response => response.response)
      .map(payload => {
        const eventHub = payload.event_hub
        const mapInfo = payload.map_info
        return {
          eventHub: {
            url: eventHub.url,
            idlePeriodToPing: eventHub.idle_period_to_ping,
            reconnectDelayMax: eventHub.reconnect_delay_max,
            reconnectDelayMin: eventHub.reconnect_delay_min
          },
          mapInfo: {
            dimension: {
              width: mapInfo.dimension[0],
              height: mapInfo.dimension[1]
            },
            size: {
              width: mapInfo.size[0],
              height: mapInfo.size[1]
            }
          }
        } as Config
      })
  }

  /**
   * Get map object for given ID
   * @param id id of map object to get from server
   */
  getMapObject (id: string): Rx.Observable<MapObject> {
    return this.sendRequest({
      path: `/map_objects/${id}`
      // XXX
    }).map(response => parseMapObject(response.response))
  }

  /**
   * Get image comments
   * @param id id of map object to get comments for
   */
  getPhotoComments (id: string): Rx.Observable<Array<Comment>> {
    return this.sendRequest({
      path: `/images/${id}/comments`
      // TODO: parse comment here
    }).map(response => parseComments(response.response))
  }

  /**
   * Post image comments
   * @param id id of map object to post comment to
   * @param content content of comment
   */
  postPhotoComment (id: string, content: string): Rx.Observable<Comment> {
    return this.sendRequest({
      method: 'POST',
      path: `/images/${id}/comments`,
      params: {
        content
      }
      // TODO: parse comment here
    }).map(response => parseComment(response.response))
  }

  /**
   * Login to server
   * @param usernameOrEmail username or email to login
   * @param password password to login
   * @param version version of app
   * @param build build of app
   */
  login (
    usernameOrEmail: string,
    password: string,
    version: string,
    build: string
  ): Rx.Observable<LoginResponse> {
    return this.sendRequest({
      path: '/authenticate',
      method: 'POST',
      params: {
        username_or_email: usernameOrEmail,
        password,
        version,
        build
      }
    })
      .map(response => response.response)
      .map(parseLoginResponse)
  }

  /**
   * Get user for given ID
   * @param userID user id to get
   */
  getUser (userID: string): Rx.Observable<User> {
    return this.sendRequest({
      path: `/users/${userID}`
    })
      .map(response => response.response)
      .map(parseUser)
  }

  /**
   * Create a guest user
   * @param password password for guest user
   */
  createGeustUser (password: string): Rx.Observable<User> {
    return this.sendRequest({
      path: '/users',
      method: 'POST',
      params: {
        guest: '1',
        password
      }
    })
      .map(response => response.response)
      .map(parseUser)
  }

  /**
   * Create a real user
   * @param username username of user
   * @param email email of user
   * @param password password for guest user
   */
  createUser ({
    username,
    email,
    password
  }: {
    username: string
    email: string
    password: string
  }): Rx.Observable<User> {
    return this.sendRequest({
      path: '/users',
      method: 'POST',
      params: {
        username,
        email,
        password
      }
    })
      .map(response => response.response)
      .map(parseUser)
  }

  /**
   * Create a player for given user
   * @param userID ID of user to create player
   * @param position the initial position of player
   */
  createPlayer ({
    userID,
    position
  }: {
    userID: string
    position: Point
  }): Rx.Observable<Player> {
    return this.sendRequest({
      path: `/users/${userID}/players`,
      method: 'POST',
      params: {
        'location[x]': position.x.toString(),
        'location[y]': position.y.toString()
      }
    })
      .map(response => response.response)
      .map(parsePlayer)
  }

  /**
   * Move a map object to given position
   * @param id id of map object to be moved
   * @param src source position of map object (just for referencing)
   * @param dest destination position of map object
   * @param localID local ID for distinguishing client sessions
   */
  moveMapObject ({
    id,
    src,
    dest,
    localID
  }: {
    id: string
    src: Point
    dest: Point
    localID?: string
  }): Rx.Observable<any> {
    return this.sendRequest({
      path: `/map_objects/${id}/actions/move`,
      method: 'POST',
      params: {
        // TODO: extract this method
        'source[x]': src.x,
        'source[y]': src.y,
        'destination[x]': dest.x,
        'destination[y]': dest.y,
        ...localID ? { local_id: localID } : {}
      }
    }).map(response => response.response)
  }

  postMessage ({
    id,
    message,
    localID
  }: {
    id: string
    message: string
    localID?: string
  }): Rx.Observable<any> {
    return this.sendRequest({
      path: `/map_objects/${id}/actions/speak`,
      method: 'POST',
      params: {
        message,
        ...localID ? { local_id: localID } : {}
      }
    }).map(response => response.response)
  }

  postPhoto ({
    userID,
    position,
    photo,
    note
  }: {
    userID: string
    position: Point
    photo: string
    note: string
  }): Rx.Observable<any> {
    const base64Image = photo.substr(photo.indexOf(',') + 1)
    const byteString = atob(base64Image)
    const mime = photo.substr(photo.indexOf(':') + 1, photo.indexOf(';'))
    const arrayBuffer = new ArrayBuffer(byteString.length)
    const bytesArray = new Uint8Array(arrayBuffer)
    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; ++i) {
      bytesArray[i] = byteString.charCodeAt(i)
    }
    const blob = new Blob([arrayBuffer], { type: mime })

    const formData = new FormData()
    formData.append('image', blob, 'image.jpg')
    formData.append('location[x]', position.x.toString())
    formData.append('location[y]', position.y.toString())
    formData.append('note', note)
    return this.sendRequest({
      path: `/users/${userID}/images`,
      method: 'POST',
      formData
    }).map(response => response.response)
  }

  updatePhoto ({
    id,
    loved
  }: {
    id: string
    loved: boolean
  }): Rx.Observable<void> {
    return this.sendRequest({
      path: `/images/${id}`,
      method: 'PUT',
      params: {
        loved
      }
    }).map(response => response.response)
  }

  private sendRequest ({
    path,
    method = 'GET',
    params = {},
    paramsFormat = 'URL',
    formData,
    includeAccessKey = true
  }: {
    path: string
    method?: 'GET' | 'POST' | 'PUT'
    params?: any
    paramsFormat?: 'URL' | 'JSON'
    formData?: FormData
    includeAccessKey?: boolean
  }): Rx.Observable<Rx.AjaxResponse> {
    let query: string | null = null
    const headers: { [index: string]: string } = {}
    if (includeAccessKey && this._authHeader) {
      headers['Authorization'] = this._authHeader
    }
    if (this.sessionID) {
      headers['X-Client-Session-Id'] = this.sessionID
    }
    if ((method === 'POST' || method === 'PUT') && !formData) {
      if (paramsFormat === 'URL') {
        headers['Content-Type'] = 'application/x-www-form-urlencoded'
      } else {
        headers['Content-Type'] = 'application/json'
      }
    } else if (method === 'GET') {
      const urlParams = new URLSearchParams()
      for (const key in params) {
        const value = params[key]
        urlParams.set(key, value)
      }
      query = urlParams.toString()
    }
    if (this.appVersion) {
      headers['X-Webapp-Version'] = this.appVersion
    }
    const resolvedURL =
      url.resolve(this.baseURL, path) + (query ? `?${query}` : '')
    return Rx.Observable.ajax({
      url: resolvedURL,
      body: formData ? formData : params,
      timeout: 60 * 1000,
      method,
      headers
    })
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/io/api-client/APIClient.ts